import { memo, useEffect, useLayoutEffect, useState } from 'react'
import { Heading, LoadingSection, Tabset } from '@quickmd-llc/design-system'
import { changeTabs, reset } from '../../store/management/ManagementActions'
import ServiceTypes from './ServiceTypes/ServiceTypes'
import ClinicMembers from './ClinicMembers/ClinicMembers'
import { resetNewMemberState } from '../../store/newMember/newMemberActions'
import { IntakeForms } from './IntakeForms/IntakeForms'
import { StatesSettings } from './StatesSettings/StatesSettings'
import { getAdminProfile } from '../../store/accountSettings/AccountSettingsThunk'
import { useChangeSearchParams } from '../../utils/useChangeSearchParams'
import { getManagementSearchParams } from './helpers/getManagementSearchParams'
import { searchParamsKeys, tabValues } from './constants'
import { useManagementStoreValues } from './hooks/useManagementStoreValues'
import { getFlags } from '../../store/flags/FlagsActions'
import { getOrderCancelReasons } from '../../store/lookups/lookupsActions'
import { useAppDispatch } from '../../redux/hooks/useAppDispatch'

type Tab = { name: string; isActive: boolean }

const Management = memo(() => {
  const dispatch = useAppDispatch()
  const { onChangeSearchParam, searchParams } = useChangeSearchParams()
  const { activeTab, searchValue } = getManagementSearchParams()

  const { adminProfile, loading, error, tabs } = useManagementStoreValues()

  const [searchClinicMembersText, setSearchClinicMembersTextText] = useState<string>(searchValue)
  const [localLoading, setLocalLoading] = useState<boolean>(true)
  const [isPopstateAction, setIsPopstateAction] = useState<boolean>(true)

  useLayoutEffect(() => {
    dispatch(changeTabs(activeTab))
    setLocalLoading(false)
  }, [])

  useEffect(() => {
    const resetIsPopstateActionValueTimer = () => {
      setTimeout(() => {
        setIsPopstateAction(false)
      }, 1000)
    }

    resetIsPopstateActionValueTimer()

    const toggleIsPopstateAction = () => {
      setIsPopstateAction(true)
      resetIsPopstateActionValueTimer()
    }

    window.addEventListener('popstate', toggleIsPopstateAction)

    return () => {
      window.removeEventListener('popstate', toggleIsPopstateAction)
    }
  }, [])

  useEffect(() => {
    if (isPopstateAction) {
      dispatch(changeTabs(activeTab))
      setSearchClinicMembersTextText(searchValue)
    }
  }, [isPopstateAction])

  useEffect(() => {
    setTimeout(() => {
      dispatch(reset())
    }, 3500)
  }, [error])

  useEffect(() => {
    if (!adminProfile?.Email) {
      const profileAction = getAdminProfile()
      if (profileAction !== undefined) {
        dispatch(profileAction)
      }
      getFlags()
    }

    getOrderCancelReasons()
    setTimeout(() => {
      sessionStorage.setItem('sessionOn', JSON.stringify(true))
      dispatch(resetNewMemberState())
    }, 1000)
  }, [])

  const onChangeActiveTab = (title: string): void => {
    setSearchClinicMembersTextText(searchValue)
    dispatch(changeTabs(title))

    if (title === tabValues.CLINIC_MEMBERS) {
      // The users API must know whether to fetch providers or admins. If a role param is
      // remembered (IsDoctor/IsAdmin), keep it. If not — e.g. arriving here via the view-service
      // breadcrumb, which navigates to a bare `?activeTab=Service types` and drops the role — set
      // the URL param to providers before the list query fires, matching the initial/login flow.
      const hasRole = searchParams.has(searchParamsKeys.IS_DOCTOR) || searchParams.has(searchParamsKeys.IS_ADMIN)
      if (hasRole) {
        onChangeSearchParam({ key: searchParamsKeys.ACTIVE_TAB, value: '' })
        return
      }
      const customSearchParams: Record<string, string> = {}
      searchParams.forEach((value, key) => {
        customSearchParams[key] = value
      })
      customSearchParams[searchParamsKeys.IS_DOCTOR] = 'true'
      customSearchParams[searchParamsKeys.IS_ACTIVE] = 'true'
      onChangeSearchParam({ key: searchParamsKeys.ACTIVE_TAB, value: '', customSearchParams })
      return
    }

    onChangeSearchParam({
      key: searchParamsKeys.ACTIVE_TAB,
      value: title,
    })
  }

  const activeTabValue = tabs.find((t: Tab) => t.isActive)?.name ?? tabValues.CLINIC_MEMBERS
  const tabsetItems = tabs.map((t: Tab) => ({ value: t.name, label: t.name }))

  return (
    <div className='flex flex-1 flex-col bg-app-background'>
      <div className='flex min-h-[300px] w-full min-w-[768px] flex-1 flex-col self-start px-sp-3 pb-[30px] pt-[42px]'>
        <Heading level='h1'>Management</Heading>
        <div className='mb-sp-3 mt-sp-2.5'>
          <Tabset tabs={tabsetItems} activeTab={activeTabValue} onTabChange={onChangeActiveTab} />
        </div>
        {loading || localLoading ?
          <div className='flex min-h-[calc(100vh-180px)] w-full flex-1 flex-col'>
            <LoadingSection />
          </div>
        : <>
            {tabs[0].isActive ?
              <ClinicMembers
                searchText={searchClinicMembersText}
                setSearchText={setSearchClinicMembersTextText}
                isPopstateAction={isPopstateAction}
              />
            : null}
            {tabs[1].isActive ?
              <ServiceTypes />
            : null}
            {tabs[2].isActive ?
              <IntakeForms />
            : null}
            {tabs[3].isActive ?
              <StatesSettings />
            : null}
          </>
        }
      </div>
    </div>
  )
})
export default Management
